import React, { useState, useEffect } from 'react';
import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Switch,
  Menu,
  MenuItem,
  IconButton,
  Badge,
  useMediaQuery,
  useTheme,
  Divider,
  ListItemIcon,
  ListItemText,
  styled
} from '@mui/material';
import {
  Groups as GroupsIcon,
  Payments as PaymentsIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Person as PersonIcon,
  ExitToApp as ExitToAppIcon,
  Menu as MenuIcon,
  Settings as SettingsIcon,
  Favorite as FavoriteIcon,
  Event as EventIcon,
  EventAvailable as EventAvailableIcon,
  PersonAdd as PersonAddIcon,
  CalendarToday as CalendarTodayIcon,
} from '@mui/icons-material';
import Verified from '@mui/icons-material/Verified'; 
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ProfilePictureModal from './ProfilePictureModal';
import api, { apiMultiPart } from '../../services/api';
import LogoDisplay from './LogoDisplay';
import CircleNotificationsOutlinedIcon from '@mui/icons-material/CircleNotificationsOutlined';
import NotificationsDropdown from './NotificationsDropdown';
import PublicAnnouncement from '../announcements/PublicAnnouncement';

// Create a styled AppBar component that handles iOS safe areas
const StyledAppBar = styled(MuiAppBar)(({ theme }) => ({
  // iOS safe area handling
  '@supports (padding-top: env(safe-area-inset-top))': {
    paddingTop: 'env(safe-area-inset-top)',
  },
  '@supports (padding-top: constant(safe-area-inset-top))': {
    paddingTop: 'constant(safe-area-inset-top)', // For older Safari
  },
}));

const AppBar = ({ toggleDarkMode, darkMode }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

  // Menu states
  const [groupAnchorEl, setGroupAnchorEl] = useState(null);
  const [userAnchorEl, setUserAnchorEl] = useState(null);
  const [mobileAnchorEl, setMobileAnchorEl] = useState(null);
  const [fundraiseAnchorEl, setFundraiseAnchorEl] = useState(null);
  const [eventsAnchorEl, setEventsAnchorEl] = useState(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [profilePicModalOpen, setProfilePicModalOpen] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const groupMenuOpen = Boolean(groupAnchorEl);
  const userMenuOpen = Boolean(userAnchorEl);
  const mobileMenuOpen = Boolean(mobileAnchorEl);
  const eventsMenuOpen = Boolean(eventsAnchorEl);
  const notificationsOpen = Boolean(notificationsAnchorEl);

  const [unreadNotifications, setUnreadNotifications] = useState(0);
 
  // Menu handlers
  const handleGroupMenuOpen = (event) => setGroupAnchorEl(event.currentTarget);
  const handleGroupMenuClose = () => setGroupAnchorEl(null);
  const handleUserMenuOpen = (event) => setUserAnchorEl(event.currentTarget);
  const handleUserMenuClose = () => setUserAnchorEl(null);
  const handleMobileMenuOpen = (event) => setMobileAnchorEl(event.currentTarget);
  const handleMobileMenuClose = () => setMobileAnchorEl(null);
  const handleEventsMenuOpen = (event) => setEventsAnchorEl(event.currentTarget);
  const handleEventsMenuClose = () => setEventsAnchorEl(null);
  
  const handleNotificationsOpen = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    // Centralized logout handles role-based redirect
    logout();
  };

  useEffect(() => {
    const fetchProfilePicture = async () => {
      try {
        const response = await apiMultiPart.get('/v1/api/users/user/profile-image');
        setProfilePicture(response.data.url);
      } catch (error) {
        console.error('Error fetching profile picture:', error);
      }
    };
    fetchProfilePicture();
    fetchNotificationCount();
  }, []);

  const fetchNotificationCount = async () => {
    try {
      const response = await api.get(`/v1/api/notifications/unread/count`);
      setUnreadNotifications(response.data);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } 
  };

  const handleProfilePicSave = async (url) => {
    try {
      setProfilePicture(url);
    } catch (error) {
      console.error('Error uploading profile picture:', error);
    }
  };

  // Navigation items
  const mainNavItems = [
    {
      label: 'Dashboard',
      action: () => navigate('/dashboard')
    },
    {
      label: 'Groups',
      subItems: [
        { label: 'Create Group', action: () => navigate('/groups/create-group') },
        { label: 'My Groups', action: () => navigate('/groups/enrolled-groups') },
        { label: 'Join Group', action: () => navigate('/groups/join-group') },
        { label: 'Submitted Requests', action: () => navigate('/groups/requests') }
      ]
    },
    {
      label: 'Events',
      subItems: [
        { label: 'Create Event', action: () => navigate('/dashboard/events/create') },
        { label: 'All Events', action: () => navigate('/dashboard/events') },
        { label: 'Created By Me', action: () => navigate('/dashboard/events/my-events') },
        { label: 'Registered Events', action: () => navigate('/dashboard/events/registered-events') }
      ]
    },
    {
      label: 'Transactions',
      action: () => navigate('/transactions')
    },
    {
      label: 'Fundraise',
      subItems: [
        { label: 'Create Campaign', action: () => navigate('/dashboard/fundraise-campaigns/create') },
        { label: 'Donate', action: () => navigate('/dashboard/fundraise-campaigns') },
        { label: 'My Campaigns', action: () => navigate('/dashboard/fundraise-campaigns/my-campaigns') }
      ]
    }
  ];

  const secondaryNavItems = [
    {
      label: 'Contact Us',
      action: () => navigate('/dashboard/contact-us')
    }
  ];

  return (
    <>
      <StyledAppBar position="static" elevation={1}>
        <Toolbar sx={{
          minHeight: {  xs: '56px', sm: '64px' 
            // xs: '48px',  // Reduced from 64px for mobile
            // sm: '56px',  // Reduced for tablet
            // md: '64px',  // Standard for desktop
          },
          paddingTop: {
            xs: '0px',
            '@supports (padding-top: env(safe-area-inset-top))': {
              paddingTop: 'env(safe-area-inset-top)',
            },
            '@supports (padding-top: constant(safe-area-inset-top))': {
              paddingTop: 'constant(safe-area-inset-top)',
            },
          },
          justifyContent: 'space-between',
          px: { xs: 1, sm: 1.5, md: 2 } // Reduced padding for smaller screens
        }}>
          {/* Logo */}
          <LogoDisplay />

          {/* Mobile view */}
          {isMobile ? (
            <>
              <Box sx={{ flexGrow: 1 }} />

              <IconButton
                size="small"
                color="inherit"
                aria-label="notifications"
                onClick={handleNotificationsOpen}
                sx={{ mr: 0.5 }}
              >
                <Badge badgeContent={unreadNotifications} color="error">
                  <CircleNotificationsOutlinedIcon fontSize="small" />
                </Badge>
              </IconButton>

              <IconButton
                size="small"
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleMobileMenuOpen}
                sx={{mr:1.0}}
              >
                <MenuIcon fontSize="large" />
              </IconButton>

              {/* Mobile Menu */}
              <Menu
                anchorEl={mobileAnchorEl}
                open={mobileMenuOpen}
                onClose={handleMobileMenuClose}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                sx={{ 
                  ml: 2,
                  '& .MuiPaper-root': {
                    marginTop: {
                      xs: '40px',
                    }
                  }
                }}
              >
                {mainNavItems.map((item, index) => (
                  item.subItems ? (
                    <Box key={index}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          px: 2, 
                          py: 1, 
                          fontWeight: 'bold',
                          color: 'text.primary'
                        }}
                      >
                        {item.label}
                      </Typography>
                      {item.subItems.map((subItem, subIndex) => (
                        <MenuItem
                          key={subIndex}
                          onClick={() => {
                            subItem.action();
                            handleMobileMenuClose();
                          }}
                          sx={{ 
                            pl: 3, 
                            py: 1,
                            minHeight: '40px'
                          }}
                        >
                          <ListItemText 
                            primary={subItem.label} 
                            sx={{ 
                              '& .MuiTypography-root': {
                                fontSize: '0.875rem'
                              }
                            }}
                          />
                        </MenuItem>
                      ))}
                      {index < mainNavItems.length - 1 && <Divider />}
                    </Box>
                  ) : (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        item.action();
                        handleMobileMenuClose();
                      }}
                      sx={{ 
                        py: 1,
                        minHeight: '40px'
                      }}
                    >
                      <ListItemText 
                        primary={item.label} 
                        sx={{ 
                          '& .MuiTypography-root': {
                            fontSize: '0.875rem',
                            fontWeight: 'bold'
                          }
                        }}
                      />
                    </MenuItem>
                  )
                ))}

                {/* Secondary navigation items in mobile */}
                <Divider />
                {secondaryNavItems.map((item, index) => (
                  <MenuItem
                    key={index}
                    onClick={() => {
                      item.action();
                      handleMobileMenuClose();
                    }}
                    sx={{ 
                      py: 1,
                      minHeight: '40px'
                    }}
                  >
                    <ListItemText 
                      primary={item.label} 
                      sx={{ 
                        '& .MuiTypography-root': {
                          fontSize: '0.875rem',
                          fontWeight: 'bold'
                        }
                      }}
                    />
                  </MenuItem>
                ))}

                <Divider />

                {/* Dark Mode Toggle */}
                <MenuItem sx={{ py: 1, minHeight: '40px' }}>
                  <ListItemText 
                    primary="Dark Mode" 
                    sx={{ 
                      '& .MuiTypography-root': {
                        fontSize: '0.875rem',
                        fontWeight: 'bold'
                      }
                    }}
                  />
                  <Switch
                    checked={darkMode}
                    onChange={toggleDarkMode}
                    color="secondary"
                    size="small"
                  />
                </MenuItem>

                {user && (
                  <>
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        navigate('/profile');
                        handleMobileMenuClose();
                      }}
                      sx={{ py: 1, minHeight: '40px' }}
                    >
                      <ListItemText 
                        primary="Profile" 
                        sx={{ 
                          '& .MuiTypography-root': {
                            fontSize: '0.875rem',
                            fontWeight: 'bold'
                          }
                        }}
                      />
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setProfilePicModalOpen(true);
                        handleMobileMenuClose();
                      }}
                      sx={{ py: 1, minHeight: '40px' }}
                    >
                      <ListItemText 
                        primary="Upload Profile Picture" 
                        sx={{ 
                          '& .MuiTypography-root': {
                            fontSize: '0.875rem',
                            fontWeight: 'bold'
                          }
                        }}
                      />
                    </MenuItem>
                    <MenuItem 
                      onClick={handleLogout} 
                      sx={{ py: 1, minHeight: '40px' }}
                    >
                      <ListItemText 
                        primary="Logout" 
                        sx={{ 
                          '& .MuiTypography-root': {
                            fontSize: '0.875rem',
                            fontWeight: 'bold'
                          }
                        }}
                      />
                    </MenuItem>
                  </>
                )}
              </Menu>
            </>
          ) : (
            <>
              {/* Desktop/Tablet Navigation */}
              <Box sx={{
                display: 'flex',
                flexGrow: 1,
                justifyContent: 'center',
                gap: 0.5,
                '& .MuiButton-root': {
                  fontSize: { md: '0.75rem', lg: '0.8rem' },
                  px: { md: 0.75, lg: 1.5 },
                  py: 0.75,
                  textTransform: 'none',
                  fontWeight: 'medium',
                  minWidth: 'fit-content'
                }
              }}>
                {/* Main navigation items */}
                {mainNavItems.map((item, index) => {
                  // Show all items on tablet as well (removed previous limit)

                  return item.subItems ? (
                    <React.Fragment key={index}>
                      <Button
                        color="inherit"
                        startIcon={item.icon}
                        endIcon={<ArrowDropDownIcon />}
                        onClick={(e) => {
                          if (item.label === 'Groups') handleGroupMenuOpen(e);
                          else if (item.label === 'Fundraise') setFundraiseAnchorEl(e.currentTarget);
                          else if (item.label === 'Events') handleEventsMenuOpen(e);
                        }}
                      >
                        {item.label}
                      </Button>
                    </React.Fragment>
                  ) : (
                    <Button
                      key={index}
                      color="inherit"
                      startIcon={item.icon}
                      onClick={item.action}
                    >
                      {item.label}
                    </Button>
                  );
                })}

                {/* Secondary navigation items (shown only on desktop) */}
                {!isTablet && secondaryNavItems.map((item, index) => (
                  <Button
                    key={index}
                    color="inherit"
                    startIcon={item.icon}
                    onClick={item.action}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>

              {/* Right-aligned icons */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  color="inherit"
                  onClick={handleNotificationsOpen}
                  size={isTablet ? 'small' : 'medium'}
                  sx={{ p: 0.75 }}
                >
                  <Badge badgeContent={unreadNotifications} color="error">
                    <CircleNotificationsOutlinedIcon fontSize={isTablet ? 'small' : 'medium'} />
                  </Badge>
                </IconButton>

                <Switch
                  checked={darkMode}
                  onChange={toggleDarkMode}
                  color="secondary"
                  size={isTablet ? 'small' : 'medium'}
                  inputProps={{ 'aria-label': 'toggle dark mode' }}
                />

                {user && (
                  <>
                    <Button
                      color="inherit"
                      startIcon={
                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                          <Avatar
                            alt={user.firstName}
                            src={profilePicture}
                            sx={{
                              width: isTablet ? 22 : 26,
                              height: isTablet ? 22 : 26,
                              mr: 0.5
                            }}
                          />
                          {user.isAccountVerified && (
                            <Verified 
                              fontSize="small"
                              color="primary"
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                right: 0,
                                width: 14,
                                height: 14,
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                border: '1px solid white'
                              }}
                            />
                          )}
                        </Box>
                      }
                      endIcon={<ArrowDropDownIcon />}
                      onClick={handleUserMenuOpen}
                      sx={{
                        minWidth: 'fit-content',
                        textTransform: 'none',
                        fontSize: isTablet ? '0.75rem' : '0.8rem',
                        px: 1,
                        py: 0.75
                      }}
                    >
                      {isTablet ? user.firstName[0] : user.firstName}
                    </Button>

                    <Menu
                      anchorEl={userAnchorEl}
                      open={userMenuOpen}
                      onClose={handleUserMenuClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      sx={{
                        '& .MuiMenuItem-root': {
                          py: 0.75
                        }
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          navigate('/profile');
                          handleUserMenuClose();
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: '36px' }}><SettingsIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Profile</ListItemText>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setProfilePicModalOpen(true);
                          handleUserMenuClose();
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: '36px' }}><PersonIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Upload Profile Picture</ListItemText>
                      </MenuItem>
                      <Divider />
                      <MenuItem onClick={handleLogout}>
                        <ListItemIcon sx={{ minWidth: '36px' }}><ExitToAppIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Logout</ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Box>
            </>
          )}
        </Toolbar>
      </StyledAppBar>

      {/* Notifications Dropdown */}
      <NotificationsDropdown
        anchorEl={notificationsAnchorEl}
        open={notificationsOpen}
        onClose={handleNotificationsClose}
        unreadCount={unreadNotifications}
        onUnreadCountChange={setUnreadNotifications}
      />

      {/* Group Menu */}
      <Menu
        anchorEl={groupAnchorEl}
        open={groupMenuOpen}
        onClose={handleGroupMenuClose}
        MenuListProps={{
          'aria-labelledby': 'groups-button',
        }}
        sx={{
          '& .MuiMenuItem-root': {
            py: 0.75
          }
        }}
      >
        <MenuItem onClick={() => { navigate('/groups/create-group'); handleGroupMenuClose(); }}>
          <ListItemIcon sx={{ minWidth: '36px' }}><GroupsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Create Group</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { navigate('/groups/enrolled-groups'); handleGroupMenuClose(); }}>
          <ListItemIcon sx={{ minWidth: '36px' }}><GroupsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>My Groups</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { navigate('/groups/join-group'); handleGroupMenuClose(); }}>
          <ListItemIcon sx={{ minWidth: '36px' }}><GroupsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Join Group</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { navigate('/groups/submitted-requests'); handleGroupMenuClose(); }}>
          <ListItemIcon sx={{ minWidth: '36px' }}><GroupsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Submitted Requests</ListItemText>
        </MenuItem>
      </Menu>

      {/* Events Menu */}
      <Menu
        anchorEl={eventsAnchorEl}
        open={eventsMenuOpen}
        onClose={handleEventsMenuClose}
        MenuListProps={{
          'aria-labelledby': 'events-button',
        }}
        sx={{
          '& .MuiMenuItem-root': {
            py: 0.75
          }
        }}
      >
        <MenuItem onClick={() => { navigate('/dashboard/events/create'); handleEventsMenuClose(); }}>
          <ListItemIcon sx={{ minWidth: '36px' }}><EventIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Create Event</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { navigate('/dashboard/events'); handleEventsMenuClose(); }}>
          <ListItemIcon sx={{ minWidth: '36px' }}><PersonAddIcon fontSize="small" /></ListItemIcon>
          <ListItemText>All Events</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { navigate('/dashboard/events/my-events'); handleEventsMenuClose(); }}>
          <ListItemIcon sx={{ minWidth: '36px' }}><EventAvailableIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Created By Me</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { navigate('/dashboard/events/registered-events'); handleEventsMenuClose(); }}>
          <ListItemIcon sx={{ minWidth: '36px' }}><CalendarTodayIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Registered Events</ListItemText>
        </MenuItem>
      </Menu>

      {/* Fundraise Menu */}
      <Menu
        anchorEl={fundraiseAnchorEl}
        open={Boolean(fundraiseAnchorEl)}
        onClose={() => setFundraiseAnchorEl(null)}
        sx={{
          '& .MuiMenuItem-root': {
            py: 0.75
          }
        }}
      >
        <MenuItem onClick={() => {
          navigate('/dashboard/fundraise-campaigns/create');
          setFundraiseAnchorEl(null);
        }}>
          <ListItemIcon sx={{ minWidth: '36px' }}><PaymentsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Create Campaign</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          navigate('/dashboard/fundraise-campaigns');
          setFundraiseAnchorEl(null);
        }}>
          <ListItemIcon sx={{ minWidth: '36px' }}><FavoriteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Donate</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          navigate('/dashboard/fundraise-campaigns/my-campaigns');
          setFundraiseAnchorEl(null);
        }}>
          <ListItemIcon sx={{ minWidth: '36px' }}><ListAltIcon fontSize="small" /></ListItemIcon>
          <ListItemText>My Campaigns</ListItemText>
        </MenuItem>
      </Menu>

      {/* Profile Picture Modal */}
      <ProfilePictureModal
        open={profilePicModalOpen}
        onClose={() => setProfilePicModalOpen(false)}
        onSave={handleProfilePicSave}
      />
    </>
  );
};

export default AppBar;